/**
 * SupporterModal Component
 *
 * Modal dialog explaining the supporter program and providing
 * checkout/management options.
 *
 * For free users: Shows benefits and CTA to start trial
 * For supporters: Shows status and link to manage subscription
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSupporter } from '../contexts/SupporterContext';
import './SupporterModal.css';

interface SupporterModalProps {
  onClose: () => void;
}

export default function SupporterModal({ onClose }: SupporterModalProps) {
  const { isAuthenticated } = useAuth();
  const {
    status,
    trialEndsAt,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    isSupporter,
    startCheckout,
    openPortal,
  } = useSupporter();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format date for display
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const handleStartCheckout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await startCheckout();
      // Will redirect to Stripe
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await openPortal();
      // Will redirect to Stripe portal
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open subscription manager');
      setIsLoading(false);
    }
  };

  // Close on escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Use portal to render at document body level to escape stacking contexts
  // This ensures the modal appears above mobile navigation, sidebars, and 3D viewer widgets
  return createPortal(
    <div
      className="supporter-modal-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="supporter-modal-title"
    >
      <div className="supporter-modal">
        {/* Close button */}
        <button
          className="supporter-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>

        {/* Header */}
        <div className="supporter-modal-header">
          <span className="supporter-modal-heart">&#x2764;</span>
          <h2 id="supporter-modal-title">
            {isSupporter ? 'Thank You for Your Support!' : 'Help Keep Hipponics Firewatch Running'}
          </h2>
        </div>

        {/* Content - varies based on subscription status */}
        <div className="supporter-modal-content">
          {!isAuthenticated ? (
            <p className="supporter-modal-message">
              Please log in to become a supporter.
            </p>
          ) : isSupporter ? (
            // Active supporter view
            <>
              <p className="supporter-modal-message">
                Your support helps cover hosting costs and enables us to keep
                improving Hipponics Firewatch for everyone.
              </p>

              <div className="supporter-modal-status">
                <div className="supporter-modal-status-row">
                  <span className="supporter-modal-status-label">Status:</span>
                  <span className={`supporter-modal-status-value status-${status}`}>
                    {status === 'trialing' ? 'Free Trial' : 'Active'}
                    {cancelAtPeriodEnd && ' (Canceling)'}
                  </span>
                </div>

                {status === 'trialing' && trialEndsAt && (
                  <div className="supporter-modal-status-row">
                    <span className="supporter-modal-status-label">Trial ends:</span>
                    <span className="supporter-modal-status-value">
                      {formatDate(trialEndsAt)}
                    </span>
                  </div>
                )}

                {currentPeriodEnd && (
                  <div className="supporter-modal-status-row">
                    <span className="supporter-modal-status-label">
                      {cancelAtPeriodEnd ? 'Access until:' : 'Renews:'}
                    </span>
                    <span className="supporter-modal-status-value">
                      {formatDate(currentPeriodEnd)}
                    </span>
                  </div>
                )}
              </div>

              <div className="supporter-modal-features">
                <h3>Coming Soon for Supporters</h3>
                <ul>
                  <li>
                    <span className="feature-icon">&#x1F5FA;</span>
                    <span>High-resolution map layers</span>
                    <span className="feature-badge">Coming Soon</span>
                  </li>
                  <li>
                    <span className="feature-icon">&#x1F525;</span>
                    <span>ML-powered fire spread prediction</span>
                    <span className="feature-badge">Coming Soon</span>
                  </li>
                  <li>
                    <span className="feature-icon">&#x1F6F0;</span>
                    <span>On-demand satellite imagery</span>
                    <span className="feature-badge">Coming Soon</span>
                  </li>
                </ul>
              </div>

              <button
                className="supporter-modal-button secondary"
                onClick={handleOpenPortal}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Manage Subscription'}
              </button>
            </>
          ) : (
            // Free user / conversion view
            <>
              <p className="supporter-modal-message">
                Hipponics Firewatch is a free community tool that helps Australians stay informed
                during bushfire season. Your support helps cover hosting costs and
                enables us to add new features.
              </p>

              <div className="supporter-modal-price">
                <span className="supporter-modal-price-amount">$19</span>
                <span className="supporter-modal-price-period">/year</span>
                <span className="supporter-modal-price-trial">3-day free trial</span>
              </div>

              <div className="supporter-modal-features">
                <h3>Coming Soon for Supporters</h3>
                <ul>
                  <li>
                    <span className="feature-icon">&#x1F5FA;</span>
                    <span>High-resolution map layers</span>
                    <span className="feature-badge">Coming Soon</span>
                  </li>
                  <li>
                    <span className="feature-icon">&#x1F525;</span>
                    <span>ML-powered fire spread prediction</span>
                    <span className="feature-badge">Coming Soon</span>
                  </li>
                  <li>
                    <span className="feature-icon">&#x1F6F0;</span>
                    <span>On-demand satellite imagery</span>
                    <span className="feature-badge">Coming Soon</span>
                  </li>
                </ul>
              </div>

              <div className="supporter-modal-trust">
                <p>
                  Cancel anytime. No commitment.
                </p>
              </div>

              <button
                className="supporter-modal-button primary"
                onClick={handleStartCheckout}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Start Free Trial'}
              </button>
            </>
          )}

          {error && (
            <div className="supporter-modal-error">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
